import * as React from 'react';

import type { AudienceDelta } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import { useServerLearnerPaging } from 'bundles/enterprise-admin-messaging-v2/hooks/useServerLearnerPaging';

type Params = {
  /** Absent for the non-filter (manual) flow — the query is then skipped. */
  audience?: LearnerListAudience;
  /** Seed the delta when re-opening the editor. */
  initialDelta?: AudienceDelta;
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  filteredCount: number;
  loading: boolean;
  error: boolean;
  /** Re-include a previously-removed cohort member. */
  addLearner: (id: string) => void;
  /** Remove a cohort member from the selection. */
  removeLearner: (id: string) => void;
  /** The current edit relative to the base cohort (removals only). */
  delta: AudienceDelta;
};

/**
 * Server-backed editor for the "Add or remove learners" screen. Fetches the
 * query's cohort (e.g. invited learners for reminder-to-join) page by page;
 * every fetched learner is a cohort member, so a row is in the selection unless
 * the admin has removed it. Removing surfaces a "+ Add" affordance to undo.
 *
 * The edit is a removals-only delta — the (future) BE filter-regeneration API
 * will consume it; nothing is committed server-side here. (`addedIds` is kept in
 * the delta shape but unused for this flow.)
 */
export const useAudienceLearnerEditor = ({ audience, initialDelta }: Params): Result => {
  const [removedIds, setRemovedIds] = React.useState<Set<string>>(() => new Set(initialDelta?.removedIds));

  const { searchQuery, setSearchQuery, page, setPage, pageCount, learners, total, loading, error } =
    useServerLearnerPaging({ audience });

  const pageRows: LearnerRow[] = React.useMemo(
    () => learners.map((learner) => ({ learner, inList: !removedIds.has(learner.id) })),
    [learners, removedIds]
  );

  const removeLearner = React.useCallback((id: string) => {
    setRemovedIds((prev) => new Set(prev).add(id));
  }, []);

  const addLearner = React.useCallback((id: string) => {
    setRemovedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const delta: AudienceDelta = React.useMemo(() => ({ removedIds: [...removedIds], addedIds: [] }), [removedIds]);

  return {
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageCount,
    pageRows,
    filteredCount: total,
    loading,
    error,
    addLearner,
    removeLearner,
    delta,
  };
};

export default useAudienceLearnerEditor;
